import {useEffect,useState} from 'react';
type Grant={code:string;enabled:boolean;fixedOwner:boolean};
const friendly:Record<string,string>={PR:'Princeton',WC:'West Chester',YD:'Yardley'};
/** Owner screenshot Oct 28: per-location Permissions with multi-select scope. */
export function StaffLocationPermissions({staffId,locationCode}:{staffId:string;locationCode:string}){
  const [grants,setGrants]=useState<Grant[]>([]);
  const [selected,setSelected]=useState<string[]>([locationCode]);
  const [status,setStatus]=useState('Loading permissions…');
  const [busy,setBusy]=useState(false);
  const reload=async()=>{
    const response=await fetch('/api/v1/staff/team/'+encodeURIComponent(staffId)+
      '/location-permissions?'+new URLSearchParams({location:locationCode}),{
        credentials:'same-origin',cache:'no-store'});
    if(!response.ok)throw Error('Location permissions unavailable.');
    const body=await response.json() as {ok:boolean;data?:{locations:Grant[]}};
    if(!body.ok||!body.data)throw Error('Location permissions unavailable.');
    return body.data.locations;
  };
  useEffect(()=>{
    let active=true;setStatus('Loading permissions…');setGrants([]);
    setSelected([locationCode]);
    void reload().then(g=>{if(active){setGrants(g);setStatus('');}})
      .catch(()=>{if(active)setStatus('Location permissions unavailable.');});
    return ()=>{active=false;};
  },[staffId,locationCode]);
  const current=grants.find(g=>g.code===locationCode);
  const all=grants.map(g=>g.code);
  const update=async(enabled:boolean)=>{
    if(busy||current?.fixedOwner)return;
    setBusy(true);setStatus('Saving…');
    try{
      const response=await fetch('/api/v1/staff/team/'+encodeURIComponent(staffId)+
        '/location-permissions',{
          method:'PATCH',credentials:'same-origin',cache:'no-store',
          headers:{'Content-Type':'application/json'},
          body:JSON.stringify({location:locationCode,
            permission:'location.settings.manage',
            applyToLocationCodes:selected,enabled}),
        });
      if(!response.ok)throw Error('Permission change could not be saved.');
      setGrants(await reload());setStatus('Saved');
    }catch(e){setStatus(e instanceof Error?e.message:'Permission change failed.');}
    finally{setBusy(false);}
  };
  return <section className="ecfs-staff-permissions" aria-label="Location permissions">
    <p className="ecfs-permission-location-indicator">
      Editing permissions at East Coast Float Spa {friendly[locationCode]??locationCode}.
    </p>
    <h2>Location Settings</h2>
    <details className="ecfs-permission-apply">
      <summary>Apply this setting to…</summary>
      {grants.length>0&&<>
        <label><input type="checkbox" checked={selected.length===all.length}
          onChange={e=>setSelected(e.target.checked?all:[locationCode])}/>
          All Locations</label>
        {grants.map(g=><label key={g.code}>
          <input type="checkbox" checked={selected.includes(g.code)}
            disabled={g.code===locationCode}
            onChange={e=>setSelected(previous=>e.target.checked
              ?[...new Set([...previous,g.code])]:
              previous.filter(x=>x!==g.code))}/>
          {friendly[g.code]??g.code}
        </label>)}
      </>}
    </details>
    {current&&<label className="ecfs-permission-check">
      <input type="checkbox" checked={current.enabled}
        disabled={busy||current.fixedOwner}
        onChange={e=>void update(e.target.checked)}/>
      Can Edit Location Settings
      {current.fixedOwner&&<em>Owner</em>}
    </label>}
    {status&&<p role="status">{status}</p>}
  </section>;
}
